import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import * as Sentry from '@sentry/nextjs';
import useGenericDevice from 'hooks/useGenericDevice';
import useProfile from 'hooks/useProfile';
import { useTranslation } from 'next-i18next';
import React, { useCallback, useState } from 'react';
import type { FC } from 'react';

import { validEmailRegex } from 'constants/regex';

import Button from 'components/Button';
import ErrorText from 'components/ErrorText';
import Modal from 'components/Modal';
import FilledTextField from 'components/MuiCustom/FilledTextField';

import { useAppDispatch, useAppSelector } from 'redux-store';
import type { ReportIssueRequestBody } from 'redux-store/slices/profile/types';
import { setReportIssueModal } from 'redux-store/slices/ui';
import reportIssueThunk from 'redux-store/thunks/api/reportIssue';

import MessageSuccess from './MessageSuccess';

const MAX_CHARACTER_LENGTH = 1000;

const ReportIssueModal: FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const profile = useProfile();
  const { open, serial } = useAppSelector(state => state.ui.reportIssueModal);

  const [email, setEmail] = useState(profile?.email || '');
  const [success, setSuccess] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');

  const portalVersion = process.env.NEXT_PUBLIC_APP_VERSION;
  const smallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const isValidEmail = (validateEmail: string): boolean =>
    validEmailRegex.test(validateEmail);

  const firmwareVersion = useGenericDevice(serial, state => state?.fwv);

  const closeModal = useCallback(() => {
    setSuccess(false);
    setMessage('');
    setError('');
    dispatch(setReportIssueModal({ open: false }));
  }, [dispatch]);

  const handleSendReport = useCallback(async () => {
    if (!isValidEmail(email)) {
      setEmailError(
        t('common:pages.general_settings.report_problem_modal.email_error'),
      );

      return;
    }

    const data: ReportIssueRequestBody = {
      customerEmail: email,
      description: message,
      source: 'portal',
      portalVersion: portalVersion || '1.0.8',
      context: serial ? 'device' : 'general',
      ...(serial
        ? {
            device: {
              serialNumber: serial,
              firmwareVersion,
            },
          }
        : undefined),
    };

    try {
      const result = await dispatch(
        reportIssueThunk({
          data: data,
        }),
      ).unwrap();

      if (!result.success) {
        setError(result.error);
      } else {
        setSuccess(true);
        setMessage('');
        setTimeout(() => {
          closeModal();
        }, 2000);
      }
    } catch (err) {
      setError(t('common:something_went_wrong'));
      Sentry.captureException(err, {
        tags: { page: 'ReportIssueModal' },
      });
    }
  }, [
    email,
    message,
    portalVersion,
    serial,
    firmwareVersion,
    t,
    dispatch,
    closeModal,
  ]);

  return (
    <Modal open={open} onClose={closeModal} hideCloseButton={success}>
      {success ? (
        <MessageSuccess onClose={closeModal} />
      ) : (
        <>
          <Box display="flex" alignItems="center" flexDirection="column">
            <Typography variant="h2">
              {t('common:pages.general_settings.report_problem')}
            </Typography>
            <Typography
              variant="body1"
              mt={2}
              width={smallScreen ? '100%' : '70%'}
              textAlign="center"
            >
              {serial
                ? t(
                    'common:pages.general_settings.report_problem_modal.device_description',
                  )
                : t(
                    'common:pages.general_settings.report_problem_modal.description',
                  )}
            </Typography>
          </Box>
          <Box mt={5}>
            <FilledTextField
              type="text"
              value={message}
              onChange={e => setMessage(e.target.value)}
              fullWidth
              multiline
              placeholder={t(
                'common:pages.general_settings.report_problem_modal.describe_issue',
              )}
              rows={5}
              inputProps={{ maxLength: MAX_CHARACTER_LENGTH }}
            />
            <Typography variant="body2" color="grey.600" textAlign="left" m={1}>
              {message.length}/{MAX_CHARACTER_LENGTH}
            </Typography>
            <Box mt={5} mb={3}>
              <Box mb={2}>
                <FilledTextField
                  label={t('common:pages.general_settings.email')}
                  type="email"
                  value={email}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (emailError) setEmailError('');
                  }}
                  fullWidth
                  error={!!emailError}
                  helperText={emailError}
                  sx={{
                    '& .MuiInputLabel-root.Mui-error': {
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                    '& .MuiFilledInput-root.Mui-error .MuiFilledInput-input ': {
                      border: '2px solid red',
                      borderRadius: '16px',
                    },
                  }}
                />
              </Box>
              {emailError ? null : (
                <Typography variant="body2" pl={2} color="grey.600">
                  {t(
                    'common:pages.general_settings.report_problem_modal.email_info',
                  )}
                </Typography>
              )}
            </Box>
            <ErrorText error={error} mb={3} />
            <Box pt={2}>
              <Button
                fullWidth
                variant="flat"
                color="primary"
                type="submit"
                size="large"
                disabled={!message || !email}
                onClick={handleSendReport}
                data-testid="send-report"
                sx={{
                  '&.Mui-disabled': {
                    backgroundColor: 'rgba(0, 109, 182, 0.3)',
                    color: 'white',
                  },
                }}
              >
                {t(
                  'common:pages.general_settings.report_problem_modal.send_report',
                )}
              </Button>
            </Box>
          </Box>
        </>
      )}
    </Modal>
  );
};

export default ReportIssueModal;
